import React, { useEffect, useRef, useState, useCallback } from 'react';

interface MasonryLayoutProps {
  children: React.ReactElement[];
  columns?: number;
  gap?: number;
  className?: string;
}

const MasonryLayout: React.FC<MasonryLayoutProps> = ({ 
  children, 
  columns = 2, 
  gap = 16, 
  className = '' 
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLayoutComplete, setIsLayoutComplete] = useState(false);

  const layoutItems = useCallback(() => {
    const container = containerRef.current;
    if (!container || children.length === 0) return;

    // Get container width and calculate item width
    const containerWidth = container.offsetWidth;
    const itemWidth = Math.floor((containerWidth - (gap * (columns - 1))) / columns);
    
    // Initialize column heights
    const columnHeights = new Array(columns).fill(0);
    
    // Get all items
    const items = Array.from(container.children) as HTMLElement[];
    
    // Position each item
    items.forEach((item, index) => {
      // Find the shortest column
      const shortestColumnIndex = columnHeights.indexOf(Math.min(...columnHeights));
      
      // Calculate position
      const x = shortestColumnIndex * (itemWidth + gap);
      const y = columnHeights[shortestColumnIndex];
      
      // Apply positioning
      item.style.position = 'absolute';
      item.style.left = `${x}px`;
      item.style.top = `${y}px`;
      item.style.width = `${itemWidth}px`;
      
      // Update column height
      const itemHeight = item.offsetHeight;
      columnHeights[shortestColumnIndex] += itemHeight + gap;
    });
    
    // Set container height
    const maxHeight = Math.max(...columnHeights) - gap;
    container.style.height = `${maxHeight}px`;
    
    setIsLayoutComplete(true);
  }, [children.length, columns, gap]);

  useEffect(() => {
    // Reset layout state
    setIsLayoutComplete(false);
    
    // Use requestAnimationFrame to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      layoutItems();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [layoutItems]);

  useEffect(() => {
    // Handle window resize
    const handleResize = () => {
      setIsLayoutComplete(false);
      setTimeout(layoutItems, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [layoutItems]);

  // Use ResizeObserver to detect when items change size
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      setTimeout(layoutItems, 50);
    });

    // Observe all child elements
    const items = Array.from(container.children) as HTMLElement[];
    items.forEach(item => resizeObserver.observe(item));

    return () => {
      resizeObserver.disconnect();
    };
  }, [children, layoutItems]);

  return (
    <div
      ref={containerRef}
      className={`masonry-layout ${className}`}
      style={{
        position: 'relative',
        width: '100%',
        opacity: isLayoutComplete ? 1 : 0,
        transition: 'opacity 0.3s ease',
        minHeight: '200px'
      }}
    >
      {children}
    </div>
  );
};

export default MasonryLayout;